import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  FaClock, FaBook, FaGraduationCap, FaCalculator, 
  FaRulerCombined, FaChevronLeft, FaTimes, 
  FaQuestionCircle, FaCheck, FaInfoCircle, FaBullseye, FaStar,
  FaHeart, FaLightbulb, FaSpinner, FaExclamationTriangle,
  FaImage, FaArrowRight, FaArrowLeft, FaTrophy, FaMedal
} from 'react-icons/fa';

import Header from './Header';
import Footer from './Footer';

const TestQuestionsScreen = ({ 
  currentTest, 
  onTestComplete, 
  onRestartTest,
  scrollToSection 
}) => {
   const [currentLevel, setCurrentLevel] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [timeLeft, setTimeLeft] = useState(30);
  const [userAnswers, setUserAnswers] = useState([]);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [answerState, setAnswerState] = useState(null); // null, "correct", "incorrect", "timeout"
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [hearts, setHearts] = useState(currentTest?.hearts?.remaining || 3);
  const [hints, setHints] = useState(currentTest?.hints?.remaining || 2);
  const [removedOptions, setRemovedOptions] = useState([]);
  const [showHint, setShowHint] = useState(false);
  const [testCompleted, setTestCompleted] = useState(false);
  const [loadedImages, setLoadedImages] = useState({});
  const [imageErrors, setImageErrors] = useState({});
  const [isMobile, setIsMobile] = useState(false);
  const [totalPoints, setTotalPoints] = useState(0);
  
  const [showLevelComplete, setShowLevelComplete] = useState(false);
  const [levelScore, setLevelScore] = useState({ correct: 0, total: 0 });
  const [wrongAnswers, setWrongAnswers] = useState(0);
  
   const [isAnimating, setIsAnimating] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [motivationMessage, setMotivationMessage] = useState(null);
  const [showMotivation, setShowMotivation] = useState(false);

   const questionContainerRef = useRef(null);
  const timerRef = useRef(null);
  
   useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

   useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

   const backgroundIcons = useMemo(() => {
    const iconComponents = [FaBook, FaGraduationCap, FaCalculator, FaRulerCombined];
    const colors = ['blue-400', 'purple-400', 'green-400', 'yellow-500', 'pink-400', 'indigo-400', 'teal-400'];
    const numberOfIcons = isMobile ? 8 : 15;
    
    return Array.from({ length: numberOfIcons }, (_, index) => ({
      id: index,
      Icon: iconComponents[Math.floor(Math.random() * iconComponents.length)],
      color: colors[Math.floor(Math.random() * colors.length)],
      size: isMobile ? Math.random() * 10 + 10 : Math.random() * 20 + 20,
      left: Math.random() * 100,
      top: Math.random() * 100,
      rotation: Math.random() * 360,
      opacity: Math.random() * 0.06 + 0.02,
      animationDelay: Math.random() * 5
    }));
  }, [isMobile]);

   const getCurrentQuestions = () => {
    if (currentTest.levels && currentTest.levels[currentLevel]?.questions?.length > 0) {
      return currentTest.levels[currentLevel].questions || [];
    }
    return currentTest.allQuestions || [];
  };

  const getCurrentQuestionData = () => {
    const currentQuestions = getCurrentQuestions();
    return currentQuestions[currentQuestion] || null;
  };

  const getTotalQuestionsInTest = () => {
    return currentTest.levels.reduce(
      (total, level) => total + (level.questions?.length || 0), 
      currentTest.allQuestions?.length || 0
    );
  };

  const getQuestionNumber = () => {
    const totalQuestionsSoFar = currentTest.levels.slice(0, currentLevel)
      .reduce((total, level) => total + (level.questions?.length || 0), 0);
    return totalQuestionsSoFar + currentQuestion + 1;
  };
  
  const calculateProgress = () => {
    const currentNumber = getQuestionNumber();
    const totalQuestions = getTotalQuestionsInTest();
    return (currentNumber / totalQuestions) * 100;
  };

   useEffect(() => {
    if (questionContainerRef.current) {
      setIsAnimating(true);
      setTimeout(() => {
        setIsAnimating(false);
      }, 400);
    }
  }, [currentQuestion, currentLevel]);

  useEffect(() => {
    setLoadedImages({});
    setImageErrors({});
    setShowHint(false);
    setRemovedOptions([]);
    setAnswerState(null);
    setSelectedAnswer(null);
  }, [currentQuestion, currentLevel]);

   useEffect(() => {
    if (timeLeft > 0 && !testCompleted && !showLevelComplete && answerState === null) {
      timerRef.current = setTimeout(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timerRef.current);
    } else if (timeLeft === 0 && !testCompleted && !showLevelComplete && answerState === null) {
      handleTimeUp();
    }
  }, [timeLeft, testCompleted, showLevelComplete, answerState]);

   const handleImageLoad = (imageUrl) => {
    setLoadedImages(prev => ({...prev, [imageUrl]: true}));
  };

  const handleImageError = (imageUrl) => {
    setImageErrors(prev => ({...prev, [imageUrl]: true}));
  };

   const renderQuestionImage = (imageUrl) => {
    if (!imageUrl) return null;
    
    const isLoaded = loadedImages[imageUrl];
    const hasError = imageErrors[imageUrl];

    return (
      <div className="mb-4 flex justify-center">
        <div className="relative w-full max-w-2xl h-48 sm:h-56 md:h-64 rounded-xl overflow-hidden border-2 border-gray-200 bg-gray-50 shadow-lg">
          {!isLoaded && !hasError && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
              <div className="animate-pulse flex flex-col items-center">
                <FaSpinner className="animate-spin text-indigo-500 text-2xl mb-3" />
                <span className="text-gray-600 text-sm">جاري تحميل الصورة...</span>
              </div>
            </div>
          )}
          
          {hasError ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100 p-4">
              <FaExclamationTriangle className="text-yellow-500 text-3xl mb-3" />
              <span className="text-gray-600 text-center text-sm">
                تعذر تحميل صورة السؤال
              </span>
            </div>
          ) : (
            <img 
              src={imageUrl} 
              alt="صورة السؤال"
              className={`w-full h-full object-contain transition-opacity duration-500 ${
                isLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              onLoad={() => handleImageLoad(imageUrl)}
              onError={() => handleImageError(imageUrl)}
              loading="eager"
            />
          )}
        </div>
      </div>
    );
  };

  const renderOptionImage = (imageUrl, optionText, index) => {
    if (!imageUrl) return null;
    
    const isLoaded = loadedImages[imageUrl];
    const hasError = imageErrors[imageUrl];

    return (
      <div className="mb-3 flex justify-center">
        <div className="relative w-full max-w-xs h-32 sm:h-36 md:h-40 rounded-lg overflow-hidden border border-gray-200 bg-white shadow-md">
          {!isLoaded && !hasError && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
              <div className="animate-pulse flex flex-col items-center">
                <FaSpinner className="animate-spin text-indigo-500 text-lg mb-2" />
                <span className="text-gray-500 text-xs">جاري التحميل...</span>
              </div>
            </div>
          )}
          
          {hasError ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50 p-2">
              <FaExclamationTriangle className="text-gray-400 text-xl mb-1" />
              <span className="text-gray-500 text-xs text-center">تعذر تحميل الصورة</span>
            </div>
          ) : (
            <img 
              src={imageUrl} 
              alt={`صورة الخيار ${optionText}`}
              className={`w-full h-full object-contain transition-all duration-500 ${
                isLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
              }`}
              onLoad={() => handleImageLoad(imageUrl)}
              onError={() => handleImageError(imageUrl)}
              loading="lazy"
            />
          )}
        </div>
      </div>
    );
  };

   const useHint = () => {
    if (hints > 0 && !showHint) {
      const newHints = hints - 1;
      setHints(newHints);
      setShowHint(true);
      
      const currentQuestionData = getCurrentQuestionData();
      if (!currentQuestionData?.options) return;
      
      const correctAnswer = currentQuestionData.correctAnswer;
      const wrongOptions = currentQuestionData.options.filter(
        option => option !== correctAnswer
      );
      
       const numberOfOptionsToRemove = Math.min(2, wrongOptions.length);
      const shuffledWrongOptions = [...wrongOptions].sort(() => 0.5 - Math.random());
      const optionsToRemove = shuffledWrongOptions.slice(0, numberOfOptionsToRemove);
      
      setRemovedOptions(optionsToRemove);
    }
  };

   const handleSelectAnswer = (answerIndex) => {
     if (answerState !== null || isTransitioning) return;
    
    setSelectedAnswer(answerIndex);
    const currentQuestionData = getCurrentQuestionData();
    if (!currentQuestionData) return;
    
    const selectedOption = currentQuestionData.options[answerIndex];
    const isCorrect = selectedOption === currentQuestionData.correctAnswer;
    
    // Record the answer
    const newAnswers = [...userAnswers];
    const totalQuestionsSoFar = currentTest.levels.slice(0, currentLevel)
      .reduce((total, level) => total + (level.questions?.length || 0), 0);
    const currentQuestionIndex = totalQuestionsSoFar + currentQuestion;
    
    newAnswers[currentQuestionIndex] = {
      questionId: currentQuestionData.id,
      selectedAnswer: selectedOption,
      selectedIndex: answerIndex,
      correctAnswer: currentQuestionData.correctAnswer,
      isCorrect: isCorrect,
      timeSpent: 30 - timeLeft,
      questionType: currentQuestionData.questionType,
      questionImage: currentQuestionData.questionImage,
      optionsImages: currentQuestionData.optionsImages,
      questionText: currentQuestionData.questionText
    };
    
    setUserAnswers(newAnswers);
    
     if (isCorrect) {
      const pointsForQuestion = currentQuestionData.points || 1;
      setTotalPoints(prev => prev + pointsForQuestion);
      
       setAnswerState("correct");
      setShowConfetti(true);
      
       
       setTimeout(() => {
        setShowConfetti(false);
      }, 1000);
      
       setTimeout(() => {
        handleMoveToNext();
      }, 1200);
    } else {
       setAnswerState("incorrect");
      const newHearts = hearts - 1;
      setHearts(newHearts);
      setWrongAnswers(prev => prev + 1);
      
       
      if (newHearts <= 0) {
        setTimeout(() => {
          alert('لقد نفذت جميع القلوب! سيتم إعادتك إلى قائمة الاختبارات.');
          onRestartTest();
        }, 1500);
        return;
      }
      
       setTimeout(() => {
        setSelectedAnswer(null);
        setAnswerState(null);
        setShowHint(false);
        setRemovedOptions([]);
      }, 1500);
    }
  };

   const handleTimeUp = () => {
    const currentQuestionData = getCurrentQuestionData();
    if (!currentQuestionData) return;
    
     const newAnswers = [...userAnswers];
    const totalQuestionsSoFar = currentTest.levels.slice(0, currentLevel)
      .reduce((total, level) => total + (level.questions?.length || 0), 0);
    const currentQuestionIndex = totalQuestionsSoFar + currentQuestion;
    
    newAnswers[currentQuestionIndex] = {
      questionId: currentQuestionData.id,
      selectedAnswer: null,
      selectedIndex: -1,
      correctAnswer: currentQuestionData.correctAnswer,
      isCorrect: false,
      timeSpent: 30,
      timeUp: true,
      questionType: currentQuestionData.questionType,
      questionImage: currentQuestionData.questionImage,
      optionsImages: currentQuestionData.optionsImages,
      questionText: currentQuestionData.questionText
    };
    
    setUserAnswers(newAnswers);
    setWrongAnswers(prev => prev + 1);
    
     const newHearts = hearts - 1;
    setHearts(newHearts);
    
    if (newHearts <= 0) {
      setTimeout(() => {
        alert('لقد نفذت جميع القلوب! سيتم إعادتك إلى قائمة الاختبارات.');
        onRestartTest();
      }, 1000);
      return;
    }
    
     setAnswerState("timeout");
    setTimeout(() => {
      handleMoveToNext();
    }, 1500);
  };

   const handleMoveToNext = () => {
    setIsTransitioning(true);
    const currentQuestions = getCurrentQuestions();
    
    if (currentQuestion < currentQuestions.length - 1) {
       setTimeout(() => {
        setCurrentQuestion(currentQuestion + 1);
        setTimeLeft(30);
        setAnswerState(null);
        setSelectedAnswer(null);
        setShowHint(false);
        setRemovedOptions([]);
        setIsTransitioning(false);
      }, 300);
    } else {
       handleLevelCompletion();
    }
  };

  const handleLevelCompletion = () => {
     const currentQuestions = getCurrentQuestions();
    const levelStartIndex = currentTest.levels.slice(0, currentLevel)
      .reduce((total, level) => total + (level.questions?.length || 0), 0);
    
    const levelAnswers = userAnswers.slice(
      levelStartIndex, 
      levelStartIndex + currentQuestions.length
    );
    
    const correctAnswers = levelAnswers.filter(answer => answer && answer.isCorrect).length;
    
    setLevelScore({
      correct: correctAnswers,
      total: currentQuestions.length
    });
    
     setTimeout(() => {
      setShowLevelComplete(true);
      setIsTransitioning(false);
    }, 300);
  };

   const getLevelCompletionMessage = () => {
     return {
      title: "انتقال للمستوى التالي",
      message: "",
      type: "normal",
      color: "from-indigo-500 to-blue-600",
      bgColor: "bg-gradient-to-r from-indigo-100 to-blue-100",
      icon: FaArrowLeft
    };
  };

   const handleProceedToNextLevel = () => {
    setShowLevelComplete(false);
    
    if (currentLevel < (currentTest.levels?.length || 1) - 1) {
       setCurrentLevel(currentLevel + 1);
      setCurrentQuestion(0);
      setTimeLeft(30);
      setAnswerState(null);
      setSelectedAnswer(null);
    } else {
       setTestCompleted(true);
      onTestComplete(userAnswers);
    }
  };

   if (testCompleted) {
    return null;
  }

  const currentQuestionData = getCurrentQuestionData();
  const currentQuestions = getCurrentQuestions();
  
   if (!currentQuestionData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">لا توجد أسئلة متاحة</p>
          <button 
            onClick={onRestartTest}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg"
          >
            العودة للقائمة
          </button>
        </div>
      </div>
    );
  }

   const questionNumber = getQuestionNumber();
  const totalQuestions = getTotalQuestionsInTest();
  const progressPercentage = calculateProgress();

   const filteredOptions = showHint && removedOptions.length > 0 
    ? currentQuestionData.options.filter(option => !removedOptions.includes(option))
    : currentQuestionData.options;
  
   const levelMessage = getLevelCompletionMessage();

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 overflow-x-hidden" dir="rtl">
      <Header scrollToSection={scrollToSection} />
      
      <div className="relative py-4 bg-gray-50 overflow-hidden">
         {backgroundIcons.map((icon) => (
          <icon.Icon
            key={icon.id}
            className={`absolute text-${icon.color} floating`}
            style={{
              left: `${icon.left}%`,
              top: `${icon.top}%`,
              fontSize: `${icon.size}px`,
              transform: `rotate(${icon.rotation}deg)`,
              opacity: icon.opacity,
              animationDelay: `${icon.animationDelay}s`
            }}
          />
        ))}

        <div className="relative px-3 mx-auto max-w-4xl">
          <div className="bg-white rounded-xl shadow-md mb-4 overflow-hidden">
            <div className={`h-1 ${currentTest.bgColor || 'bg-indigo-600'}`}></div>
            <div className="p-4 p-mini-header">
              <div className="test-info-container flex flex-wrap justify-between items-center gap-3">
                <div className="test-title-section flex items-center">
                  <div className="mr-3">
                    {currentTest.icon || <FaQuestionCircle className="text-indigo-600 text-xl sm:text-2xl" />}
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900 header-title">{currentTest.title}</h2>
                    <p className="text-xs text-gray-600 header-subtitle">
                      {currentTest.levels?.[currentLevel]?.levelTitle || `المستوى ${currentLevel + 1}`}
                    </p>
                  </div>
                </div>
                  
                <div className="test-stats-container flex items-center space-x-2 space-x-reverse">
                   <div className="hearts-container flex items-center text-red-500">
                    {[...Array(hearts)].map((_, i) => (
                      <FaHeart key={i} className="ml-1" />
                    ))}
                    {[...Array((currentTest?.hearts?.total || 3) - hearts)].map((_, i) => (
                      <FaHeart key={`empty-${i}`} className="ml-1 text-gray-300" />
                    ))}
                  </div>
                  
                   <button 
                    onClick={useHint}
                    disabled={hints === 0 || showHint || answerState !== null}
                    className={`hint-button flex items-center ${hints === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-yellow-500 hover:text-yellow-600'} transition-colors`}
                  >
                    <span className="ml-1 text-sm font-semibold hint-text">{hints}</span>
                    <FaLightbulb />
                    <span className="hint-label mr-1 text-sm">تلميح</span>
                  </button>
                  
                   <div className={`time-container flex items-center ${timeLeft < 10 ? 'text-red-500 animate-pulse' : timeLeft < 20 ? 'text-orange-500' : 'text-indigo-500'} font-semibold ml-3 p-1.5 bg-gray-50 rounded-lg text-sm`}>
                    <FaClock className="ml-1.5" />
                    <span className="tabular-nums">{timeLeft}</span>
                    <span className="text-xs mr-1">ث</span>
                  </div>
                  
                   <div className="score-container flex items-center text-gray-600 font-semibold p-1.5 bg-gray-50 rounded-lg text-sm">
                    <FaBullseye className="ml-1.5 text-indigo-500" />
                    <span className="tabular-nums">{totalPoints}</span>
                    <span className="text-xs mr-1">نقطة</span>
                  </div>
                </div>
              </div>
              
               <div className="mt-4 mb-1">
                <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                  <div 
                    className="h-full rounded-full transition-all duration-1000 bg-gradient-to-r from-indigo-500 to-purple-600"
                    style={{ width: `${progressPercentage}%` }}
                  ></div>
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>سؤال {questionNumber} من {totalQuestions}</span>
                  <span>المستوى {currentLevel + 1} من {currentTest.levels?.length || 1}</span>
                </div>
                
                 <div className="flex items-center text-red-500 ml-3 mt-1">
                  <FaTimes className="ml-1" />
                  <span className="tabular-nums text-xs">الأخطاء: {wrongAnswers}</span>
                </div>
              </div>
            </div>
          </div>

           <div 
            ref={questionContainerRef}
            className={`bg-white rounded-xl shadow-md overflow-hidden transition-all duration-500 ${isAnimating ? 'transform translate-y-4 opacity-0' : 'transform translate-y-0 opacity-100'}`}
          >
            <div className="p-4 sm:p-5">
               <div className="mb-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                    {currentQuestionData.questionText}
                  </h3>
                  
                   <button 
                    onClick={useHint}
                    disabled={hints === 0 || showHint || answerState !== null}
                    className={`question-hint-button p-1.5 rounded-full transition-all ${hints === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-indigo-500 hover:bg-indigo-50'}`}
                    aria-label="تلميح"
                  >
                    <div className="flex items-center">
                      <FaInfoCircle className="text-base ml-1" />
                      <span className="inline-hint-text text-xs">تلميح</span>
                    </div>
                  </button>
                </div>
                
                 {currentQuestionData.questionImage && 
                  renderQuestionImage(currentQuestionData.questionImage)
                }
                
                 {showHint && (
                  <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3 mb-4 text-indigo-700 text-sm">
                    <div className="flex items-start">
                      <FaLightbulb className="ml-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-semibold mb-1">تم إزالة خيارين خاطئين!</p>
                        <p className="text-xs">باقي {filteredOptions.length} خيار{filteredOptions.length > 2 ? 'ات' : ''}</p>
                        <p className="text-xs mt-1 text-indigo-600">✓ تم استخدام تلميح</p>
                      </div>
                    </div>
                  </div>
                )}
                
                 <div className={`space-y-3 ${
                  currentQuestionData.questionType === 'image-options' ? 'grid grid-cols-1 sm:grid-cols-2 gap-4' : ''
                }`}>
                  {filteredOptions.map((option, index) => {
                    const originalIndex = currentQuestionData.options.indexOf(option);
                    const isSelected = selectedAnswer === originalIndex;
                    const isCorrect = selectedAnswer !== null && option === currentQuestionData.correctAnswer;
                    const isWrong = selectedAnswer !== null && isSelected && !isCorrect;
                    const optionImage = currentQuestionData.optionsImages?.[originalIndex];
                    
                    let optionClasses = "w-full text-right p-3 sm:p-3.5 border rounded-lg transition-all duration-300 flex flex-col items-stretch";
                    
                     if (isCorrect && answerState === "correct") {
                      optionClasses += " border-green-500 bg-green-50 shadow-sm";
                    }
                     else if (isWrong) {
                      optionClasses += " border-red-500 bg-red-50 shadow-sm";
                    }
                     else if (answerState === "timeout" && option === currentQuestionData.correctAnswer) {
                      optionClasses += " border-yellow-500 bg-yellow-50 shadow-sm";
                    }
                     else if (answerState === "incorrect") {
                       if (isSelected) {
                        optionClasses += " border-red-500 bg-red-50 shadow-sm";
                      } else {
                        optionClasses += " border-gray-200 bg-gray-50 opacity-70 cursor-not-allowed";
                      }
                    }
                     else if (answerState === null) {
                      optionClasses += " border-gray-200 hover:border-indigo-400 hover:bg-indigo-50 cursor-pointer";
                    }
                     else {
                      optionClasses += " border-gray-200 bg-gray-50 opacity-70 cursor-not-allowed";
                    }
                    
                    return (
                      <button
                        key={originalIndex}
                        onClick={() => answerState === null && handleSelectAnswer(originalIndex)}
                        className={optionClasses}
                        disabled={answerState !== null}
                      >
                         {optionImage && renderOptionImage(optionImage, option, originalIndex)}
                        
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-gray-900 text-sm font-medium">
                            {currentQuestionData.questionType === 'image-options' ? `خيار ${index + 1}` : option}
                          </span>
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center transition-all ${
                            isSelected 
                              ? isCorrect
                                ? "bg-green-500 text-white"
                                : "bg-red-500 text-white"
                              : answerState === "timeout" && option === currentQuestionData.correctAnswer
                                ? "bg-yellow-500 text-white"
                                : "border border-gray-300"
                          }`}>
                            {(isSelected && isCorrect) || 
                             (answerState === "timeout" && option === currentQuestionData.correctAnswer) ? (
                              <FaCheck className="text-xs" />
                            ) : null}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

               <div className="border-t pt-4">
                <div className="flex flex-wrap gap-1.5 justify-center">
                  {Array.from({ length: currentQuestions.length }).map((_, index) => {
                    const totalQuestionsSoFar = currentTest.levels.slice(0, currentLevel)
                      .reduce((total, level) => total + (level.questions?.length || 0), 0);
                    const questionIndex = totalQuestionsSoFar + index;
                    const isAnswered = userAnswers[questionIndex]?.selectedAnswer !== undefined;
                    const isCurrent = index === currentQuestion;
                    const isCorrect = isAnswered && userAnswers[questionIndex]?.isCorrect;
                    
                    return (
                      <div
                        key={index}
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold transition-all duration-300 ${
                          isCurrent 
                            ? 'bg-indigo-600 text-white border border-indigo-600 scale-110 shadow-sm' 
                            : isCorrect 
                              ? 'bg-green-500 text-white border border-green-500'
                              : isAnswered
                                ? 'bg-red-500 text-white border border-red-500'
                                : 'bg-gray-100 text-gray-600 border border-gray-200'
                        }`}
                      >
                        {index + 1}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
       {showConfetti && answerState === "correct" && (
        <div className="confetti-container">
          {Array.from({ length: 50 }).map((_, i) => (
            <div 
              key={i} 
              className="confetti" 
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                backgroundColor: ['#4F46E5', '#10B981', '#F59E0B', '#EC4899', '#8B5CF6'][Math.floor(Math.random() * 5)],
                width: `${Math.random() * 10 + 5}px`,
                height: `${Math.random() * 10 + 5}px`,
              }}
            ></div>
          ))}
        </div>
      )}
      
       {showLevelComplete && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-3 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl p-4 sm:p-5 max-w-sm w-full mx-auto transform celebration">
            <div className="text-center">
               <div className="mb-4 flex justify-center">
                <div className={`p-3 rounded-full ${levelMessage.bgColor}`}>
                  <levelMessage.icon className="text-3xl text-indigo-500" />
                </div>
              </div>
              
               <h3 className="text-xl font-bold text-gray-900 mb-6">
                {levelMessage.title}
              </h3>
              
               <button
                onClick={handleProceedToNextLevel}
                className={`w-full bg-gradient-to-r ${levelMessage.color} text-white font-semibold py-3 px-4 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-md text-sm flex items-center justify-center`}
              >
                {currentLevel < (currentTest.levels?.length || 1) - 1 ? (
                  <>
                    <span>متابعة</span>
                    <FaArrowLeft className="mr-2" />
                  </>
                ) : (
                  <>
                    <span>إنهاء الاختبار</span>
                    <FaCheck className="mr-2" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      <Footer scrollToSection={scrollToSection} />

      <style jsx>{`
        .floating {
          animation: float 8s ease-in-out infinite;
          pointer-events: none;
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0) rotate(var(--rotation, 0deg));
          }
          50% {
            transform: translateY(-10px) rotate(var(--rotation, 0deg));
          }
        }

        /* Level Completion Animation */
        .celebration {
          animation: celebration 0.7s ease-out;
        }

        @keyframes celebration {
          0% {
            transform: scale(0.7);
            opacity: 0;
          }
          50% {
            transform: scale(1.05);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        /* Confetti Effect */
        .confetti-container {
          position: fixed;
          inset: 0;
          pointer-events: none;
          z-index: 40;
          overflow: hidden;
        }

        .confetti {
          position: absolute;
          top: -10px;
          border-radius: 2px;
          animation: confetti-fall 3s linear forwards;
        }

        @keyframes confetti-fall {
          0% {
            transform: translateY(0) rotate(0deg) scale(1);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg) scale(0.5);
            opacity: 0;
          }
        }

        /* Hint Button Styling */
        .hint-button {
          display: flex !important;
          align-items: center !important;
          gap: 2px !important;
          font-size: 11px !important;
        }

        .hint-label {
          display: inline !important;
          font-size: 10px !important;
        }

        .hint-text {
          font-size: 11px !important;
        }

        .inline-hint-text {
          display: inline !important;
        }

        /* Image Optimizations */
        img {
          image-rendering: auto;
          -webkit-backface-visibility: hidden;
          -webkit-perspective: 1000;
          -webkit-transform: translate3d(0,0,0);
          -webkit-font-smoothing: antialiased;
        }

        /* Mobile Optimizations */
        @media (max-width: 400px) {
          /* Header Adjustments */
          .p-mini-header {
            padding: 8px 12px !important;
          }
          
          .test-info-container {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 6px !important;
          }
          
          .header-title {
            font-size: 14px !important;
            line-height: 1.2 !important;
          }
          
          .header-subtitle {
            font-size: 10px !important;
          }
          
          .test-title-section {
            order: 2;
            width: 100%;
            justify-content: flex-end;
          }
          
          .test-stats-container {
            order: 1;
            width: 100%;
            justify-content: space-between !important;
            margin-bottom: 4px;
          }
          
          /* Stats Container Grid Layout */
          .test-stats-container {
            display: grid !important;
            grid-template-columns: 1fr 1fr !important;
            grid-template-rows: auto auto !important;
            gap: 6px !important;
          }
          
          .hearts-container {
            grid-column: 2;
            grid-row: 1;
            justify-content: flex-end;
            order: 1;
          }
          
          .hint-button {
            grid-column: 1;
            grid-row: 1;
            justify-content: flex-start;
            order: 2;
          }
          
          .time-container {
            grid-column: 2;
            grid-row: 2;
            justify-content: flex-end;
            order: 3;
            margin-left: 0 !important;
            padding: 4px 8px !important;
          }
          
          .score-container {
            grid-column: 1;
            grid-row: 2;
            justify-content: flex-start;
            order: 4;
            padding: 4px 8px !important;
          }
          
          /* Text Size Adjustments */
          .text-lg {
            font-size: 16px !important;
          }
          
          .text-sm {
            font-size: 11px !important;
          }
          
          .text-xs {
            font-size: 10px !important;
          }
          
          /* Element Size Adjustments */
          .w-5.h-5 {
            width: 16px !important;
            height: 16px !important;
          }
          
          .w-6.h-6 {
            width: 18px !important;
            height: 18px !important;
            font-size: 9px !important;
          }
          
          /* Spacing Adjustments */
          .p-4 {
            padding: 12px !important;
          }
          
          .mb-6 {
            margin-bottom: 16px !important;
          }
          
          .question-hint-button {
            padding: 4px 8px !important;
          }
        }
        
        /* Very Small Devices */
        @media (max-width: 350px) {
          .test-stats-container {
            grid-template-columns: 1fr 1fr !important;
            gap: 4px !important;
          }
          
          .hint-button {
            gap: 1px !important;
          }
          
          .hint-label {
            font-size: 9px !important;
          }
          
          .hint-text {
            font-size: 10px !important;
          }
          
          .time-container,
          .score-container {
            font-size: 10px !important;
            padding: 3px 6px !important;
          }
          
          .hearts-container svg {
            width: 13px !important;
            height: 13px !important;
          }
          
          .question-hint-button {
            padding: 2px 6px !important;
            font-size: 10px !important;
          }
          
          .inline-hint-text {
            font-size: 9px !important;
          }
        }
        
        /* Medium Mobile Devices */
        @media (max-width: 767px) {
          .floating {
            animation: float 10s ease-in-out infinite;
          }
          
          .confetti {
            width: 6px;
            height: 6px;
          }
          
          .inline-hint-text {
            display: inline-block;
            font-size: 10px;
            margin-right: 1px;
          }
        }

        /* Tablet+ Devices */
        @media (min-width: 768px) {
          .inline-hint-text {
            display: none;
          }
        }

        /* Small Devices Image Containers */
        @media (max-width: 480px) {
          .question-image-container {
            height: 200px;
          }
          
          .option-image-container {
            height: 120px;
          }
        }
      `}</style>
    </div>
  );
};

export default TestQuestionsScreen;